import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import { isFiniteNumber } from '../../shared/checklistUtils';
import { formatBooleanDataPointValue, formatMoney, formatOptionalNumber } from '../../shared/format';
import type { CampaignRowView } from '../../tabs/campaign/types';
import type { WithChecklistItemMetadata } from '../../types';
import { presentRowBase, warnMissingRuleText } from '../rowBase';

import {
  CAMPAIGN_RULE_TEXT,
  DATE_CHANGES_DISALLOWED_EVIDENCE,
  SOURCE_UNAVAILABLE_EVIDENCE,
  SOURCE_UNAVAILABLE_VALUE,
  formatStringValue,
  getCampaignSummary,
  getPolicyBibleEvidence,
} from './campaignRuleText';

const STANDARD_FLASH_CAMPAIGN_TYPE = 'FLASH - standard';

type CampaignDetail = Pick<CampaignRowView, 'dataPoints' | 'evidence' | 'value'> &
  Partial<Pick<CampaignRowView, 'action' | 'approvalNote' | 'packageName' | 'policyBibleMatch' | 'summary'>>;

export function presentCampaignRows(
  rows: Array<CmChecklist.CampaignRow>,
): Array<WithChecklistItemMetadata<CampaignRowView>> {
  return rows.flatMap((row) => {
    const ruleText = CAMPAIGN_RULE_TEXT[row.rule_id];

    if (!ruleText) {
      warnMissingRuleText(row);
      return [];
    }

    const approved = row.status === 'approved';
    const detail = presentCampaignDetail(row, approved);

    return [
      {
        ...presentRowBase(row),
        ...ruleText,
        ...detail,
        summary: detail.summary ?? getCampaignSummary(ruleText.field, approved),
      },
    ];
  });
}

// A row blocked by a failed source carries evidence built from empty inputs, which would read as blank values.
function presentCampaignDetail(row: CmChecklist.CampaignRow, approved: boolean): CampaignDetail {
  if (row.reason_codes.includes('source_unavailable')) {
    return { dataPoints: [], evidence: SOURCE_UNAVAILABLE_EVIDENCE, value: SOURCE_UNAVAILABLE_VALUE };
  }

  return presentCheckDetail(row, approved);
}

function presentCheckDetail(row: CmChecklist.CampaignRow, approved: boolean): CampaignDetail {
  const { evidence, subject } = row;

  switch (evidence.check) {
    case 'blank_text': {
      const value = formatStringValue(evidence.value, 'Nothing loaded');
      const populated = !!evidence.value?.trim();
      const noun = row.rule_id === 'campaign-payment-terms-modification' ? 'Payment' : 'Contract';

      return {
        dataPoints: [
          { label: 'Loaded value', value },
          { label: 'Expected value', value: 'Nothing loaded' },
        ],
        evidence: `${noun} terms modification is ${populated ? 'populated' : 'blank'}.`,
        value,
      };
    }
    case 'policy_bible_number': {
      const value =
        row.rule_id === 'campaign-date-change-allowed' && !evidence.date_change_allowed
          ? formatBooleanValue(evidence.date_change_allowed)
          : formatOptionalNumber(evidence.loaded ?? undefined);

      return {
        dataPoints: [
          { label: 'Loaded value', value },
          { label: 'Expected value', value: formatOptionalNumber(evidence.expected ?? undefined) },
        ],
        evidence: row.reason_codes.includes('date_changes_disallowed')
          ? DATE_CHANGES_DISALLOWED_EVIDENCE
          : getPolicyBibleEvidence({
              approved,
              expected: evidence.expected,
              hasRule: !!evidence.policy_bible,
              loaded: evidence.loaded,
            }),
        policyBibleMatch: evidence.policy_bible,
        value,
      };
    }
    case 'policy_bible_text': {
      const value = formatStringValue(evidence.loaded);

      return {
        dataPoints: [
          { label: 'Loaded value', value },
          { label: 'Expected value', value: formatStringValue(evidence.expected) },
        ],
        evidence: getPolicyBibleEvidence({
          approved,
          expected: evidence.expected,
          hasRule: !!evidence.policy_bible,
          loaded: evidence.loaded,
        }),
        policyBibleMatch: evidence.policy_bible,
        value,
      };
    }
    case 'booking_cutoff': {
      const blank = evidence.loaded == null;
      const value = blank ? 'Blank' : String(evidence.loaded);

      return {
        action: blank ? `Populate booking data cut-off with ${evidence.expected}.` : undefined,
        approvalNote: blank ? 'Booking data cut-off needs to be populated before approval.' : undefined,
        dataPoints: [
          { label: 'Loaded value', value },
          { label: 'Expected value', value: String(evidence.expected) },
        ],
        evidence: blank
          ? `Booking data cut-off is blank and should be populated to ${evidence.expected}.`
          : 'Booking data cut-off is populated.',
        value,
      };
    }
    case 'boolean_expected': {
      if (row.rule_id === 'campaign-sem-support') {
        const semSupport = evidence.loaded ? 'Yes' : 'No';

        return {
          dataPoints: [
            { label: 'Loaded value', value: semSupport },
            { label: 'Expected value', value: 'No' },
          ],
          evidence: evidence.loaded ? 'SEM support is enabled.' : 'SEM support is not enabled.',
          value: semSupport,
        };
      }

      const value = formatBooleanValue(evidence.loaded);

      return {
        dataPoints: [
          { label: 'Loaded value', value },
          { label: 'Expected value', value: 'Yes' },
        ],
        evidence: evidence.loaded
          ? 'Package upgrades are allowed.'
          : 'Package upgrades are not allowed or source value is missing.',
        value,
      };
    }
    case 'campaign_type': {
      const isStandard = evidence.loaded === STANDARD_FLASH_CAMPAIGN_TYPE;

      return {
        action: isStandard ? 'Add note that flexi nights will not pull through to contract.' : undefined,
        approvalNote: isStandard ? 'Flexi nights will not pull through to contract.' : undefined,
        dataPoints: [
          { label: 'Loaded value', value: formatStringValue(evidence.loaded) },
          { label: 'Expected value', value: evidence.expected },
        ],
        evidence: approved
          ? 'Campaign type matches the expected Flash value for the loaded deal options.'
          : 'Campaign type is missing or does not match the expected Flash value for the loaded deal options.',
        value: formatStringValue(evidence.loaded),
      };
    }
    case 'book_by_window':
      return {
        dataPoints: [
          { label: 'Book By date', value: formatStringValue(evidence.book_by) },
          { label: 'Travel To date', value: formatStringValue(evidence.travel_to) },
          { label: 'Expected window', value: `${evidence.min_days} to ${evidence.max_days} days before Travel To` },
        ],
        evidence:
          evidence.days_before_travel_to == null
            ? 'Book By or Travel To date is missing or invalid.'
            : `Book By is ${evidence.days_before_travel_to} days before Travel To.`,
        value: `${formatStringValue(evidence.book_by)} / ${formatStringValue(evidence.travel_to)}`,
      };
    case 'flexi_deal_options': {
      const count = evidence.flexi_package_count;
      const summary = approved
        ? 'Flexi-night setup aligns with loaded deal options.'
        : 'Flexi-night setup needs review.';
      const countCopy = `${count} flexi deal option${count === 1 ? '' : 's'}`;

      return {
        dataPoints: [
          { label: 'Campaign type', value: evidence.campaign_type || 'Not available' },
          { label: 'Flexi deal options', value: String(count) },
          { label: 'Flexi option names', value: subject.flexi_package_names.join(', ') || 'None' },
        ],
        evidence: summary,
        summary,
        value:
          evidence.is_flexi_campaign === null
            ? `Campaign type unavailable; ${countCopy}`
            : `${evidence.campaign_type}; ${countCopy}`,
      };
    }
    case 'flexi_night_margin': {
      const margins = evidence.margins.map((entry, index) => ({
        margin: entry.margin,
        name: flexiPackageName(subject, index, entry.package_id),
      }));
      const summary = approved ? 'Flexi night margins are within approved range.' : 'Flexi night margins need review.';

      return {
        dataPoints:
          margins.length > 0
            ? margins.map(({ margin, name }) => ({
                label: name,
                value: isFiniteNumber(margin) ? `${margin}%` : 'Not available',
              }))
            : [{ label: 'Flexi deal options', value: 'None' }],
        evidence: summary,
        summary,
        value:
          margins.length > 0
            ? margins.map(({ margin, name }) => `${name}: ${isFiniteNumber(margin) ? `${margin}%` : 'N/A'}`).join('; ')
            : 'No flexi-night deal options',
      };
    }
    case 'suggested_value': {
      const suggested = formatOptionalCurrency(evidence.suggested_value, evidence.currency_code);
      const value = formatOptionalCurrency(evidence.value, evidence.currency_code);

      return {
        dataPoints: [
          { label: 'Suggested value', value: suggested },
          { label: 'Value', value },
        ],
        evidence: getSuggestedValueEvidence(row, approved),
        packageName: subject.package_name ?? undefined,
        value: `Suggested value: ${suggested} / Value: ${value}`,
      };
    }
    default:
      return { dataPoints: [], evidence: '', value: '' };
  }
}

function getSuggestedValueEvidence(row: CmChecklist.CampaignRow, approved: boolean) {
  if (row.reason_codes.includes('suggested_value_missing')) {
    return 'Suggested value is missing from opportunity deal data.';
  }

  if (row.reason_codes.includes('value_missing')) {
    return 'Value is missing from package price data.';
  }

  return approved ? 'Suggested value matches Value.' : 'Suggested value does not match Value.';
}

function flexiPackageName(subject: CmChecklist.CampaignSubject, index: number, packageId: string | null) {
  // svc-offer emits "" (not omitted) for a package without a display name to keep this array
  // index-aligned with margins/discounts, so an empty string must fall through too.
  return subject.flexi_package_names[index] || packageId || 'Deal option';
}

function formatBooleanValue(value?: boolean | null) {
  return formatBooleanDataPointValue({
    dataAvailable: value != null,
    enabled: value ?? false,
    unavailableValue: 'Not available',
  });
}

function formatOptionalCurrency(value: number | null, currencyCode: string | null) {
  if (!isFiniteNumber(value)) {
    return 'N/A';
  }

  return currencyCode ? formatMoney(value, currencyCode) : String(value);
}
